'use server';

import { signIn, auth } from '@/lib/auth';
import { AuthError } from 'next-auth';
import { headers } from 'next/headers';
import { checkRateLimit } from '@/lib/rate-limiter';
import { origemDaRequisicao } from '@/lib/origem-da-requisicao';
import { getServerDictionary } from '@/lib/i18n/server';

// Define State Type
export type AuthState = {
    message?: string;
    success?: boolean;
    role?: string;
    isPlatformStaff?: boolean;
};

export async function authenticate(
    prevState: AuthState | undefined,
    formData: FormData,
): Promise<AuthState> {
    const hdrs = await headers();
    // Fonte única — ver `origem-da-requisicao.ts`. Mesma chave da rota de proxy do login.
    const ip = origemDaRequisicao(hdrs);
    const email = formData.get('email') as string;
    const rateLimitKey = `auth:login:${ip}:${email}`;

    if (!(await checkRateLimit(rateLimitKey, 5, 60_000))) {
        // Fora do i18n até 30/08. O muro passou a funcionar de verdade nesta rodada (a chave
        // era forjável), então o lojista em `en` começaria a receber português.
        const { dict } = await getServerDictionary();
        return { message: dict.login.tooManyAttempts, success: false };
    }

    try {
        await signIn('credentials', {
            ...Object.fromEntries(formData),
            redirect: false, // Prevent server-side redirect loop
        });

        // Fetch user from DB to get the reliable role (avoiding session race condition)
        const email = formData.get("email") as string;
        const user = await import("@/lib/prisma").then(m => m.prisma.user.findUnique({
            where: { email },
            select: { id: true, role: true, isPlatformStaff: true, userStores: { select: { storeId: true }, take: 1 } }
        }));

        if (user) {
            const { AuditService, AuditAction, AuditResource } = await import("@/services/audit.service");
            await AuditService.log({
                action: AuditAction.LOGIN,
                resource: AuditResource.USER,
                userId: user.id,
                storeId: user.userStores[0]?.storeId ?? undefined,
                details: { email },
            });
        }

        return {
            success: true,
            message: 'Login realizado com sucesso!',
            role: user?.role,
            isPlatformStaff: user?.isPlatformStaff === true
        };
    } catch (error) {
        if (error instanceof AuthError) {
            // Check for 2FA error hidden in cause
            const cause = error.cause as any;
            if (cause?.err?.message === "2FA_REQUIRED" || error.message.includes("2FA_REQUIRED")) {
                return { message: "2FA_REQUIRED", success: false };
            }
            if (cause?.err?.message === "Código 2FA inválido." || error.message.includes("Código 2FA inválido.")) {
                return { message: "Código inválido. Tente novamente.", success: false };
            }

            switch (error.type) {
                case 'CredentialsSignin':
                    return { message: 'Credenciais inválidas.', success: false };
                case 'CallbackRouteError':
                    // Sometimes custom errors wrap here
                    if (cause?.err?.message === "2FA_REQUIRED") return { message: "2FA_REQUIRED", success: false };
                    if (cause?.err?.message === "Código 2FA inválido.") return { message: "Código inválido. Tente novamente.", success: false };
                    return { message: 'Erro ao processar login.', success: false };
                default:
                    return { message: 'Algo deu errado.', success: false };
            }
        }
        // Check standard Error if not wrapped in AuthError yet (sometimes local dev behavior)
        if (error instanceof Error) {
            if (error.message.includes("2FA_REQUIRED")) return { message: "2FA_REQUIRED", success: false };
            if (error.message.includes("Código 2FA inválido")) return { message: "Código inválido. Tente novamente.", success: false };
        }
        // If signIn throws other errors (unexpected), capture them
        console.error("Auth Error:", error);
        return { message: 'Erro no servidor.', success: false };
    }
}
